import React, { useEffect, useRef, useState } from 'react';

import CustomPeriod from './CustomPeriod';
import AddFilter from './AddFilter';
import MultiPick from './MultiPick';
import RangePick from './RangePick';
import ParamControls from './ParamControls';
import { memberLabel, presetLabel } from './format';
import { switchesInTitles, withEntry } from './lensState';
import type { LensDocument, LensState, SqlParam } from './types';
import type { BoardEditor } from './useBoardEdit';

export type ControlsPlacement = 'top' | 'side';

type LensControlsProps = {
  document: LensDocument;
  state: LensState;
  change?: (next: LensState) => void;
  filterLabel: (column: string) => string;
  options: (column: string) => Array<string>;
  placement: ControlsPlacement;
  narrow: boolean;
  open: boolean;
  onOpen: (open: boolean) => void;
  withPeriod?: boolean;
  only?: 'period';
  lens?: { id: string; version: number };
  editor?: BoardEditor;
};

const SIDE_FROM = 7;
const SEARCH_FROM = 10;
const RAIL_CAP = 6;

function controlCount(document: LensDocument): number {
  const inTitles = switchesInTitles(document);
  const params = Object.entries(document.params).filter(([name, param]) => 'sql' in param && param.options.length > 0 && !inTitles.has(name)).length;
  return (document.period.presets.length > 1 ? 1 : 0) + params + document.filters.length;
}

function filterCount(document: LensDocument): number {
  const inTitles = switchesInTitles(document);
  return Object.entries(document.params).filter(([name, param]) => 'sql' in param && param.options.length > 0 && !inTitles.has(name)).length + document.filters.filter((filter) => filter.style !== 'segmented').length;
}

function panelParams(document: LensDocument): Set<string> {
  const inTitles = switchesInTitles(document);
  return new Set(Object.entries(document.params).filter(([name, param]) => 'sql' in param && param.options.length > 0 && !inTitles.has(name)).map(([name]) => name));
}

export function filtersSet(document: LensDocument, state: LensState): number {
  const params = panelParams(document);
  return Object.keys(state.params ?? {}).filter((name) => params.has(name) && (document.params[name] as SqlParam).role !== 'measure').length + Object.values(state.filters ?? {}).filter((values) => values?.length).length;
}

export function hasFilterControls(document: LensDocument): boolean {
  return panelParams(document).size > 0 || document.filters.length > 0;
}

export function placementOf(document: LensDocument): ControlsPlacement {
  const asked = document.display.filters ?? 'auto';
  if (asked !== 'auto') return asked;
  return controlCount(document) >= SIDE_FROM ? 'side' : 'top';
}

export default function LensControls({
  document,
  state,
  change,
  filterLabel,
  options,
  placement,
  narrow,
  open,
  onOpen,
  withPeriod = true,
  only,
  lens,
  editor,
}: LensControlsProps) {
  const [match, setMatch] = useState('');
  const [everything, setEverything] = useState(false);
  const matches = (column: string) => filterLabel(column).toLowerCase().includes(match.trim().toLowerCase());

  const period =
    withPeriod && (document.period.presets.length > 1 || change) ? (
      <div aria-label="Period" className="seg" role="group">
        {document.period.presets.map((preset) => (
          <button
            className={(state.period ?? document.period.default) === preset ? 'on' : ''}
            disabled={!change}
            key={preset}
            onClick={() => change?.({ ...state, period: preset === document.period.default ? undefined : preset })}
            type="button"
          >
            {presetLabel(preset)}
          </button>
        ))}
        <CustomPeriod disabled={!change} editor={editor} document={document} onPick={(next) => change?.({ ...state, period: next })} period={state.period} presets={document.period.presets} />
      </div>
    ) : null;
  if (only === 'period') return period;
  const switches = document.filters
    .filter((filter) => filter.style === 'segmented' && matches(filter.column))
    .map((filter) => {
      const chosen = state.filters?.[filter.column]?.[0];
      return (
        <div aria-label={filterLabel(filter.column)} className="seg" key={filter.column} role="group">
          <button
            className={chosen ? '' : 'on'}
            disabled={!change}
            onClick={() => change?.({ ...state, filters: withEntry(state.filters, filter.column, undefined) })}
            type="button"
          >
            All
          </button>
          {[...options(filter.column)]
            .sort()
            .map((option) => (
              <button
                className={chosen === option ? 'on' : ''}
                disabled={!change}
                key={option}
                onClick={() => change?.({ ...state, filters: withEntry(state.filters, filter.column, [option]) })}
                type="button"
              >
                {memberLabel(option)}
              </button>
            ))}
        </div>
      );
    });
  const isSet = (column: string) => Boolean(state.filters?.[column]?.length || state.ranges?.[column]);
  const ranged = (column: string, onRemove?: () => void) => (
    <RangePick
      key={column}
      label={filterLabel(column)}
      onPick={change ? (next) => change({ ...state, ranges: withEntry(state.ranges, column, next ?? (onRemove ? {} : undefined)) }) : undefined}
      onRemove={onRemove}
      range={state.ranges?.[column] ?? document.filters.find((filter) => filter.column === column)?.range}
    />
  );
  const picker = (filter: LensDocument['filters'][number]) =>
    filter.style === 'range' ? (
      ranged(filter.column)
    ) : (
      <MultiPick
        key={filter.column}
        label={filterLabel(filter.column)}
        onPick={change ? (next) => change({ ...state, filters: withEntry(state.filters, filter.column, next) }) : undefined}
        options={[...options(filter.column)].sort()}
        picked={state.filters?.[filter.column] ?? []}
        single={filter.multiple === false}
      />
    );
  const pickable = document.filters.filter((filter) => filter.style !== 'segmented' && matches(filter.column));
  const declared = new Set(document.filters.map((filter) => filter.column));
  const remove = (key: 'filters' | 'ranges', column: string) => change?.({ ...state, [key]: withEntry(state[key] as Record<string, unknown> | undefined, column, undefined) });
  const extras = [
    ...Object.keys(state.filters ?? {})
      .filter((column) => !declared.has(column) && matches(column))
      .map((column) => (
        <MultiPick
          key={column}
          label={filterLabel(column)}
          onPick={change ? (next) => change({ ...state, filters: withEntry(state.filters, column, next) }) : undefined}
          onRemove={change ? () => remove('filters', column) : undefined}
          options={[...options(column)].sort()}
          picked={state.filters?.[column] ?? []}
        />
      )),
    ...Object.keys(state.ranges ?? {})
      .filter((column) => !declared.has(column) && matches(column))
      .map((column) => ranged(column, change ? () => remove('ranges', column) : undefined)),
  ];
  const adder =
    change && lens ? (
      <AddFilter
        key="add-filter"
        lensId={lens.id}
        lensVersion={lens.version}
        onAdd={(column, numeric) => change(numeric ? { ...state, ranges: { ...state.ranges, [column]: {} } } : { ...state, filters: { ...state.filters, [column]: [] } })}
        taken={new Set([...declared, ...Object.keys(state.filters ?? {}), ...Object.keys(state.ranges ?? {})])}
      />
    ) : null;
  const pickers = [...pickable.map(picker), ...extras, adder];

  const paramNames = Object.keys(document.params).filter((name) => panelParams(document).has(name));
  const capped = !everything && !match.trim();
  const paramsShown = capped ? paramNames.filter((name, index) => index < RAIL_CAP || state.params?.[name] !== undefined) : paramNames;
  const room = Math.max(0, RAIL_CAP - paramsShown.length);
  const declaredShown = capped ? pickable.filter((filter, index) => index < room || isSet(filter.column)) : pickable;
  const hiddenCount = paramNames.length - paramsShown.length + pickable.length - declaredShown.length;
  const showToggle = !match.trim() && (everything ? paramNames.length + pickable.length > RAIL_CAP : hiddenCount > 0);

  if (placement === 'top')
    return (
      <>
        {period}
        <ParamControls change={change} document={document} state={state} />
        {switches}
        {pickers}
      </>
    );

  if (!open) return period;
  return (
    <>
      {period}
      <SidePanel narrow={narrow} onOpen={onOpen}>
        {controlCount(document) >= SEARCH_FROM ? (
          <input
            aria-label="Find a filter"
            className="side-find"
            onChange={(event) => setMatch(event.target.value)}
            placeholder="Find a filter"
            value={match}
          />
        ) : null}
        {switches.length ? (
          <section>
            <h4>Switches</h4>
            {switches}
          </section>
        ) : null}
        <section>
          {switches.length ? <h4>Filters</h4> : null}
          <ParamControls all change={change} document={document} match={match} names={paramsShown} state={state} />
          {declaredShown.map(picker)}
          {extras}
          {showToggle ? (
            <button className="side-more" onClick={() => setEverything(!everything)} type="button">
              {everything ? 'Fewer filters' : `More filters (${hiddenCount})`}
            </button>
          ) : null}
          {adder}
        </section>
        {narrow ? (
          <button className="side-done" onClick={() => onOpen(false)} type="button">
            Done
          </button>
        ) : null}
      </SidePanel>
    </>
  );
}

function SidePanel({ narrow, onOpen, children }: { narrow: boolean; onOpen: (open: boolean) => void; children: React.ReactNode }) {
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!narrow) return undefined;
    const outside = (event: MouseEvent) => !panel.current?.contains(event.target as Node) && onOpen(false);
    const escape = (event: KeyboardEvent) => event.key === 'Escape' && onOpen(false);
    window.document.addEventListener('mousedown', outside);
    window.document.addEventListener('keydown', escape);
    return () => {
      window.document.removeEventListener('mousedown', outside);
      window.document.removeEventListener('keydown', escape);
    };
  }, [narrow, onOpen]);
  return (
    <div aria-label="Filters" className={narrow ? 'side open sheet' : 'rail-panel'} data-lens-filters ref={panel} role="group">
      <div className="side-body">{children}</div>
    </div>
  );
}
